import React, { useRef, useState, useMemo, useCallback } from 'react';
import { Box, Center, Text, Icon, VStack, HStack, IconButton, Tooltip, Button, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure } from '@chakra-ui/react';
import { MdTimer, MdArrowBackIos, MdArrowForwardIos, MdSchedule } from 'react-icons/md';
import { FaPlay, FaPause } from 'react-icons/fa';
import { getTranslations } from '../translations';
import { getLocalizedIcon, getIconById } from '../data/iconRepository';

const TimerDisplay = ({
    timeLeft,
    totalTimeLeft,
    workTime,
    restTime,
    rounds,
    currentPhase,
    startupTime,
    currentIconId,
    currentLabel,
    exercises,
    isDarkMode,
    isActive,
    isPaused,
    currentRound,
    goToNextStep,
    goToPreviousStep,
    startTimer,
    profileName,
    hasSavedConfigs,
    togglePause,
    resetTimer,
    requestFullscreen,
    exitFullscreen,
    isFullscreen,
    restTimeRules
}) => {
    // Calcola il round corrente effettivo (considerando che countdown è prima del round 1)
    const effectiveRound = currentPhase === 'countdown' ? 0 : currentRound;
    const t = getTranslations();

    // Helper function to format seconds as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Helper functions for styles
    const getBackgroundColor = () => {
        switch (currentPhase) {
            case 'countdown': return 'brand.100';
            case 'work': return 'brand.300';
            case 'rest': return 'brand.500';
            default: return 'brand.200';
        }
    };

    // Helper function to get rest time for a specific round (same logic as useWorkoutTimer)
    // Usa useMemo per ricalcolare quando cambiano le dipendenze
    const getRestTimeForRound = React.useCallback((round) => {
        if (!restTimeRules || restTimeRules.length === 0) {
            return restTime;
        }

        // Valuta le regole in ordine inverso (le più specifiche per ultime)
        const reversedRules = [...restTimeRules].reverse();
        for (let rule of reversedRules) {
            if (rule.type === 'after' && round >= rule.round) {
                return rule.restTime;
            }
            if (rule.type === 'every' && round % rule.interval === 0) {
                return rule.restTime;
            }
            if (rule.type === 'specific' && rule.rounds && rule.rounds.includes(round)) {
                return rule.restTime;
            }
        }
        return restTime;
    }, [restTimeRules, restTime]);

    // Calcola il tempo totale dell'allenamento (usato anche prima dell'avvio)
    // Usa useMemo per ricalcolare quando cambiano le dipendenze
    const calculatedTotalTime = React.useMemo(() => {
        let totalRestTime = 0;
        for (let r = 1; r < rounds; r++) {
            totalRestTime += getRestTimeForRound(r);
        }
        return startupTime + (workTime * rounds) + totalRestTime;
    }, [startupTime, workTime, rounds, getRestTimeForRound]);

    // Derived values for SVG
    // maxTotalTime deve includere anche startupTime per il calcolo corretto della circonferenza
    const maxTotalTime = Math.max(1, calculatedTotalTime);
    
    // currentPhaseDuration deve usare il tempo di riposo corretto per il round corrente durante la fase di riposo
    const currentRestTime = currentPhase === 'rest' ? getRestTimeForRound(currentRound) : restTime;
    const currentPhaseDuration = Math.max(1, (currentPhase === 'work' ? workTime : currentPhase === 'rest' ? currentRestTime : startupTime));

    // Circular progress calculations - aumentate del 20%
    const radiusOuter = 162; // 135 * 1.2
    const circumferenceOuter = 2 * Math.PI * radiusOuter;
    const offsetOuter = circumferenceOuter * (1 - (totalTimeLeft / maxTotalTime));

    const radiusInner = 132; // 110 * 1.2
    const circumferenceInner = 2 * Math.PI * radiusInner;
    // Fix: Ensure timeLeft is not negative and doesn't exceed duration (though logic should handle it)
    const offsetInner = circumferenceInner * (1 - (Math.max(0, timeLeft) / currentPhaseDuration));

    const strokeColorInner = currentPhase === 'work' ? '#22C55E' : currentPhase === 'rest' ? '#F97316' : '#EAB308';
    const trackColorInner = isDarkMode 
        ? (currentPhase === 'work' ? 'rgba(34, 197, 94, 0.15)' : currentPhase === 'rest' ? 'rgba(249, 115, 22, 0.15)' : 'rgba(234, 179, 8, 0.15)')
        : (currentPhase === 'work' ? 'rgba(49, 151, 149, 0.2)' : currentPhase === 'rest' ? 'rgba(249, 115, 22, 0.3)' : 'rgba(255, 255, 255, 0.2)');
    
    // Colore circonferenza esterna - più scuro nel tema chiaro durante il riposo
    const strokeColorOuter = isDarkMode 
        ? '#EF4444' 
        : (currentPhase === 'rest' ? '#DC2626' : '#EF4444'); // Rosso più scuro per riposo nel tema chiaro

    // Icon handling - mostra icona corrente durante work, icona successiva durante countdown e rest
    let displayIconId = null;
    
    if (currentPhase === 'work') {
        displayIconId = currentIconId;
    } else if (currentPhase === 'countdown') {
        const firstExercise = exercises && exercises.length > 0 ? exercises[0] : null;
        displayIconId = firstExercise?.iconId || null;
    } else if (currentPhase === 'rest') {
        const nextExercise = exercises && exercises.length > currentRound ? exercises[currentRound] : null;
        displayIconId = nextExercise?.iconId || null;
    }
    
    const iconData = displayIconId ? getLocalizedIcon(getIconById(displayIconId)) : null;

    const getExerciseName = useCallback((exercise) => {
        if (!exercise) return null;
        if (exercise.customLabel?.trim()) return exercise.customLabel.trim();
        if (exercise.iconId) {
            const data = getLocalizedIcon(getIconById(exercise.iconId));
            return data?.name || null;
        }
        return null;
    }, []);

    const currentExerciseName = getExerciseName(exercises?.[currentRound - 1]);
    const nextExerciseName = getExerciseName(exercises?.[currentRound]);
    const firstExerciseName = getExerciseName(exercises?.[0]);

    const phaseExerciseText = useMemo(() => {
        if (currentPhase === 'rest' && nextExerciseName) {
            return `${t.prepare} ${nextExerciseName}`;
        }
        if (currentPhase === 'work' && currentExerciseName) {
            return currentExerciseName;
        }
        if (currentPhase === 'countdown' && firstExerciseName) {
            return `${t.prepare} ${firstExerciseName}`;
        }
        return null;
    }, [currentPhase, currentExerciseName, nextExerciseName, firstExerciseName, t.prepare]);

    const showSessionInfo = ((currentPhase !== 'ready' && totalTimeLeft > 0) || (currentPhase === 'ready' && !isActive));

    // Long press per reset
    const { isOpen: isResetDialogOpen, onOpen: onResetDialogOpen, onClose: onResetDialogClose } = useDisclosure();
    const cancelRef = useRef();
    const longPressTimerRef = useRef(null);
    const [isLongPressing, setIsLongPressing] = useState(false);

    const handleMouseDown = () => {
        if (!isActive || currentPhase === 'ready') return;
        
        longPressTimerRef.current = setTimeout(() => {
            setIsLongPressing(true);
            onResetDialogOpen();
        }, 500); // 500ms per long press
    };

    const handleMouseUp = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        setIsLongPressing(false);
    };

    const handleClick = (e) => {
        // Se è un long press, non fare click
        if (isLongPressing) {
            e.preventDefault();
            return;
        }
        
        // Click normale per pausa/riprendi solo se attivo
        if (isActive && currentPhase !== 'ready' && togglePause) {
            togglePause();
        }
    };

    const handleDoubleClick = (e) => {
        // Toggle fullscreen
        if (isFullscreen && exitFullscreen) {
            exitFullscreen();
        } else if (!isFullscreen && requestFullscreen) {
            requestFullscreen();
        }
    };

    const handleConfirmReset = () => {
        if (resetTimer) {
            resetTimer();
        }
        onResetDialogClose();
    };

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current);
            }
        };
    }, []);

    return (
        <>
            <VStack spacing={2} align="center" w="100%" data-timer-container>
                {/* Nascondi dinamicamente il testo "riposo modificato" solo nel TimerDisplay, non nella modale About */}
                {React.useEffect(() => {
                    const hideText = () => {
                        // Cerca solo all'interno del TimerDisplay, escludendo le modali
                        const timerContainer = document.querySelector('[data-timer-container]');
                        if (!timerContainer) return;
                        
                        const allTexts = timerContainer.querySelectorAll('p.chakra-text');
                        allTexts.forEach((text) => {
                            // Nascondi solo se contiene "riposo modificato" (non "Riposo Variabile" che è nella guida)
                            if (text.textContent && text.textContent.includes('riposo modificato')) {
                                text.style.display = 'none';
                                text.style.visibility = 'hidden';
                                text.style.opacity = '0';
                                text.style.height = '0';
                                text.style.width = '0';
                                text.style.overflow = 'hidden';
                            }
                        });
                    };
                    hideText();
                    const interval = setInterval(hideText, 100);
                    return () => clearInterval(interval);
                }, [])}
                {/* Profile name or TABATA - shown above everything */}
                {((currentPhase !== 'ready' && totalTimeLeft > 0) || (currentPhase === 'ready' && !isActive)) && (
                    <>
                        {profileName ? (
                            <Text fontSize="2xl" fontWeight="bold" color={isDarkMode ? "white" : "gray.800"} mb={-2}>
                                {profileName}
                            </Text>
                        ) : !hasSavedConfigs ? (
                            <Text fontSize="2xl" fontWeight="bold" color={isDarkMode ? "white" : "gray.800"} mb={-2}>
                                TABATA
                            </Text>
                        ) : null}
                    </>
                )}
                {/* Fase, cronometro e round - stessa riga sopra il cerchio */}
                {showSessionInfo && (
                    <HStack w="100%" justify="space-between" align="center" mb={-6} px={1} spacing={2}>
                        <Box flex={1} minW={0} textAlign="left">
                            {currentPhase !== 'ready' && (
                                <Text
                                    fontSize="xl"
                                    fontWeight="bold"
                                    color={isDarkMode ? "white" : "gray.800"}
                                    noOfLines={1}
                                >
                                    {t[currentPhase]}
                                </Text>
                            )}
                        </Box>
                        <HStack spacing={2} align="center" flexShrink={0}>
                            <Icon as={MdTimer} boxSize="2.4rem" color={isDarkMode ? "gray.300" : "gray.700"} opacity={0.8} />
                            <Text fontSize="xl" fontWeight="medium" color={isDarkMode ? "gray.300" : "gray.700"} opacity={0.8} whiteSpace="nowrap">
                                {formatTime(currentPhase === 'ready' ? calculatedTotalTime : totalTimeLeft)}
                            </Text>
                        </HStack>
                        <HStack spacing={2} align="center" flex={1} justify="flex-end" flexShrink={0} minW={0}>
                            <Text fontSize="xl" fontWeight="bold" color={isDarkMode ? "gray.300" : "gray.700"} opacity={0.8} whiteSpace="nowrap">
                                Round {currentPhase === 'ready' ? '0' : currentRound} / {rounds}
                            </Text>
                            {restTimeRules && restTimeRules.length > 0 && (
                                <Box
                                    as="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    w="2.4rem"
                                    h="2.4rem"
                                    pointerEvents="none"
                                    aria-hidden="true"
                                    role="presentation"
                                    title=""
                                    aria-label=""
                                    data-tooltip=""
                                    sx={{
                                        '& svg': {
                                            width: '100%',
                                            height: '100%',
                                            fill: isDarkMode ? '#FCD34D' : '#EA580C',
                                            pointerEvents: 'none'
                                        }
                                    }}
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                        role="presentation"
                                        focusable="false"
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                    </svg>
                                </Box>
                            )}
                        </HStack>
                    </HStack>
                )}
                <Box 
                    position="relative" 
                    w="360px" 
                    h="360px" 
                    p={4} 
                    display="flex" 
                    alignItems="center" 
                    justifyContent="center" 
                    mx="auto"
                    onDoubleClick={handleDoubleClick}
                >
                    {/* Freccia sinistra - Vai allo step precedente */}
                    {(isActive && !isPaused && (currentPhase === 'work' || currentPhase === 'rest' || currentPhase === 'countdown')) && (
                        <IconButton
                            aria-label="Step precedente"
                            icon={<Icon as={MdArrowBackIos} boxSize="3rem" />}
                            onClick={goToPreviousStep}
                            position="absolute"
                            left="-60px"
                            top="50%"
                            transform="translateY(-50%)"
                            zIndex="2"
                            size="lg"
                            borderRadius="full"
                            bg={isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}
                            color={isDarkMode ? 'white' : 'gray.800'}
                            _hover={{
                                bg: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                                transform: 'translateY(-50%) scale(1.1)',
                            }}
                            transition="all 0.2s"
                            isDisabled={currentPhase === 'countdown'}
                            opacity={currentPhase === 'countdown' ? 0.3 : 1}
                            cursor={currentPhase === 'countdown' ? 'not-allowed' : 'pointer'}
                        />
                    )}
                    
                    {/* Freccia destra - Vai allo step successivo */}
                    {(isActive && !isPaused && (currentPhase === 'work' || currentPhase === 'rest' || currentPhase === 'countdown')) && (
                        <IconButton
                            aria-label="Step successivo"
                            icon={<Icon as={MdArrowForwardIos} boxSize="3rem" />}
                            onClick={goToNextStep}
                            position="absolute"
                            right="-60px"
                            top="50%"
                            transform="translateY(-50%)"
                            zIndex="2"
                            size="lg"
                            borderRadius="full"
                            bg={isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}
                            color={isDarkMode ? 'white' : 'gray.800'}
                            _hover={{
                                bg: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                                transform: 'translateY(-50%) scale(1.1)',
                            }}
                            transition="all 0.2s"
                            isDisabled={currentPhase === 'work' && currentRound >= rounds}
                            opacity={(currentPhase === 'work' && currentRound >= rounds) ? 0.3 : 1}
                            cursor={(currentPhase === 'work' && currentRound >= rounds) ? 'not-allowed' : 'pointer'}
                        />
                    )}
                    
                    <Center 
                        position="absolute" 
                        top="50%" 
                        left="50%" 
                        transform="translate(-50%, -50%)" 
                        zIndex="1" 
                        flexDirection="column"
                        onClick={handleClick}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onTouchStart={handleMouseDown}
                        onTouchEnd={handleMouseUp}
                        cursor={isActive && currentPhase !== 'ready' ? 'pointer' : 'default'}
                        w="100%"
                        h="100%"
                    >
                    {!isActive && (currentPhase === 'ready' || currentPhase === 'finished') ? (
                        // Bottone PLAY grande al centro quando non attivo
                        <Tooltip label={t.start} placement="top" hasArrow>
                            <Button
                                onClick={startTimer}
                                size="lg"
                                colorScheme="teal"
                                borderRadius="full"
                                w="120px"
                                h="120px"
                                boxShadow="0 4px 14px 0 rgba(0,128,128,0.39)"
                                _hover={{ 
                                    transform: 'scale(1.1)', 
                                    boxShadow: '0 6px 20px rgba(0,128,128,0.5)' 
                                }}
                                transition="all 0.2s"
                                aria-label={t.start}
                            >
                                <Icon as={FaPlay} boxSize="4rem" />
                            </Button>
                        </Tooltip>
                    ) : (
                        <>
                            {/* Bottone pausa trasparente quando in pausa */}
                            {isPaused && (
                                <Button
                                    position="absolute"
                                    size="lg"
                                    colorScheme="yellow"
                                    borderRadius="full"
                                    w="120px"
                                    h="120px"
                                    opacity={0.7}
                                    zIndex="2"
                                    pointerEvents="none"
                                    boxShadow="0 4px 14px 0 rgba(234, 179, 8, 0.39)"
                                >
                                    <Icon as={FaPause} boxSize="4rem" />
                                </Button>
                            )}
                            {iconData && (currentPhase === 'work' || currentPhase === 'countdown' || currentPhase === 'rest') && (
                                <Box mb={-2} animation="fadeIn 0.5s" zIndex="1">
                                    <Icon
                                        as={iconData.icon}
                                        boxSize="3.5rem"
                                        color={isDarkMode ? "gray.300" : "gray.700"}
                                        opacity={0.8}
                                    />
                                </Box>
                            )}
                            <Text 
                                fontSize={iconData && currentPhase === 'work' ? "6xl" : "7xl"} 
                                fontWeight="bold" 
                                lineHeight="1" 
                                color={isDarkMode ? "white" : "gray.800"}
                                zIndex="1"
                            >
                        {timeLeft}
                    </Text>
                        </>
                    )}
                </Center>
                <svg width="360" height="360" viewBox="0 0 360 360">
                    <defs>
                        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx="0" dy="0" stdDeviation="3" floodOpacity="0.3" />
                        </filter>
                    </defs>

                    {/* Background Circle */}
                    <circle cx="180" cy="180" r="162" fill="none" stroke={isDarkMode ? "rgba(200, 200, 200, 0.2)" : "rgba(128, 128, 128, 0.3)"} strokeWidth="26" filter="url(#shadow)" />

                    {/* Outer Progress (Total Time) */}
                    <circle
                        cx="180" cy="180" r="162" fill="none"
                        stroke={strokeColorOuter}
                        strokeWidth="26"
                        strokeDasharray={circumferenceOuter}
                        strokeDashoffset={offsetOuter}
                        transform="rotate(-90 180 180)"
                        style={{ transition: 'stroke-dashoffset 1s linear' }}
                        filter="url(#shadow)"
                    />

                    {/* Inner Track */}
                    <circle cx="180" cy="180" r="132" fill="none" stroke={trackColorInner} strokeWidth="43" filter="url(#shadow)" />

                    {/* Inner Progress (Current Phase Time) */}
                    <circle
                        cx="180" cy="180" r="132" fill="none"
                        stroke={strokeColorInner}
                        strokeWidth="50"
                        strokeDasharray={circumferenceInner}
                        strokeDashoffset={offsetInner}
                        transform="rotate(-90 180 180)"
                        style={{ transition: 'stroke-dashoffset 1s linear', filter: 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.3))' }}
                        opacity="0.9"
                    />
                </svg>
            </Box>
            </VStack>

            {currentPhase !== 'ready' && phaseExerciseText && (
                <Box
                    w="100%"
                    mt={-4}
                    px={4}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={4}
                >
                    {iconData && (
                        <Icon
                            as={iconData.icon}
                            boxSize="2.4rem"
                            color={isDarkMode ? "gray.300" : "gray.700"}
                            opacity={0.85}
                            flexShrink={0}
                        />
                    )}
                    <Text
                        fontSize="5xl"
                        fontWeight="bold"
                        textAlign="center"
                        color={isDarkMode ? "white" : "gray.800"}
                        noOfLines={2}
                    >
                        {phaseExerciseText}
                    </Text>
                </Box>
            )}

            {/* AlertDialog per conferma reset */}
            <AlertDialog
                isOpen={isResetDialogOpen}
                leastDestructiveRef={cancelRef}
                onClose={onResetDialogClose}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent bg={isDarkMode ? 'gray.800' : 'white'}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold" color={isDarkMode ? 'white' : 'gray.800'}>
                            {t.resetConfirmTitle || 'Conferma Reset'}
                        </AlertDialogHeader>
                        <AlertDialogBody color={isDarkMode ? 'gray.200' : 'gray.700'}>
                            {t.resetConfirmMessage || 'Sei sicuro di voler resettare il timer?'}
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onResetDialogClose} variant="ghost" color={isDarkMode ? 'gray.300' : 'gray.600'}>
                                {t.startupTimeDialogCancel || 'Annulla'}
                            </Button>
                            <Button colorScheme="red" onClick={handleConfirmReset} ml={3}>
                                {t.reset || 'RESET'}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default TimerDisplay;
